import React, { useContext, useEffect, useRef, useCallback } from "react";

// Third party
import PropTypes from "prop-types";
import { useSnackbar } from "notistack";

// Context
import { useLocalSettingsContext } from "context/LocalSettingsContext";

// Services
import {
  deleteSite,
  getAllSitesByProject,
  getAllSitesByScenario,
  getSite,
  modifySite
} from "services/siting";
import { calculateSites } from "services/scenario";

// Utils & others
import {
  olAddSite,
  olRemoveSite,
  olSetSiteColor,
  olZoomToSitesExtent
} from "utils/OpenLayers/SitesUtils";

import {
  cesiumAddSite,
  cesiumRemoveSite,
  cesiumSetSiteColor,
  cesiumZoomToSitesExtent
} from "utils/Cesium/SitesUtils";

import {
  arrayIsEqual,
  searchObjectIndexInArray,
  sortArrayAlphabetically
} from "utils/ArrayTools";
import { DEFAULT_COLOR } from "assets/global";

const clone = require("rfdc")();

// Self context
const DispatchContext = React.createContext();
const StateContext = React.createContext();

/**
 * Initial state for the Sites Context
 */
const initialState = {
  sites: null,
  scenarios: null,
  activeScenarioId: null,
  projectId: null,
  isGenerating: null,
  isFetching: false,
  error: null,
  refreshSites: null
};

/**
 * Reducer with the actions to update the Sites Context state.
 *
 * @param { object } state
 * @param { object } action
 * @returns
 */
function SitesReducer(state, action) {
  switch (action.type) {
    case "FETCHING_START": {
      return {
        ...state,
        isFetching: true,
        error: null
      };
    }
    case "FETCH_FAIL": {
      return {
        ...state,
        error: action.payload.error,
        isFetching: false
      };
    }

    case "SITES_FETCH_SUCCESS": {
      return {
        ...state,
        sites: sortArrayAlphabetically(action.payload.sites, "name"),
        scenarios: sortArrayAlphabetically(action.payload.scenarios, "name"),
        projectId: action.payload.projectId,
        isFetching: false,
        error: null
      };
    }

    case "SITE_FETCH_SUCCESS": {
      return {
        ...state,
        sites: sortArrayAlphabetically(action.payload.sites, "name"),
        isFetching: false,
        error: null
      };
    }

    case "SITE_GENERATED_SUCCESS": {
      return {
        ...state,
        sites: sortArrayAlphabetically(action.payload.sites, "name"),
        isGenerating: null,
        isFetching: false,
        error: null
      };
    }

    case "SITE_UPDATE": {
      return {
        ...state,
        sites: sortArrayAlphabetically(action.payload.sites, "name")
      };
    }

    case "SCENARIO_ACTIVE_SET": {
      return {
        ...state,
        activeScenarioId: action.payload.activeScenarioId
      };
    }

    case "ERROR_SET": {
      return {
        ...state,
        error: action.payload.error
      };
    }

    case "ERROR_RESET": {
      return {
        ...state,
        error: null
      };
    }

    case "CUSTOM_SITE_REMOVE": {
      return {
        ...state,
        sites: sortArrayAlphabetically(action.payload.sites, "name")
      };
    }

    case "SCENARIO_NAME_UPDATE": {
      return {
        ...state,
        scenarios: sortArrayAlphabetically(action.payload.scenarios, "name")
      };
    }

    case "REFRESH_SCENARIOS_SET": {
      return {
        ...state,
        refreshScenarios: action.payload.refreshScenarios
      };
    }

    case "SITES_RESET": {
      return { ...initialState };
    }

    default: {
      return state;
    }
  }
}

/**
 * Provides all children with the state and the dispatch of the
 * Sites Context.
 *
 * @param { node } children
 * @returns node
 */
function SitesProvider({
  children,
  scenario,
  scenarios,
  activeScenarioId,
  projectId
}) {
  const [state, dispatch] = React.useReducer(SitesReducer, initialState);

  const { enqueueSnackbar } = useSnackbar();

  const scenarioIds = scenarios.map(item => Number(item.id));

  const projectIdRef = useRef();
  const scenarioIdsRef = useRef([]);
  const sitesRef = useRef();

  /**
   * Effect to show errors and reset the corresponding
   * error state.
   */
  useEffect(() => {
    if (state.error) {
      enqueueSnackbar(state.error, {
        variant: "error"
      });

      dispatch({ type: "ERROR_RESET" });
    }
  }, [enqueueSnackbar, state.error]);

  /**
   * Effect to set the active scenario when changed.
   */
  useEffect(() => {
    dispatch({
      type: "SCENARIO_ACTIVE_SET",
      payload: { activeScenarioId: activeScenarioId }
    });
  }, [activeScenarioId]);

  /**
   * Effect to immediately reset sites when the project changes.
   * This unmounts stale SiteItems before clearMapLayers() runs,
   * preventing them from re-adding old OL layers to the cleared map.
   */
  useEffect(() => {
    if (projectIdRef.current && projectIdRef.current !== projectId) {
      dispatch({ type: "SITES_RESET" });
    }
  }, [projectId]);

  /**
   * Method to calculate the condition to re-fetch sites from back-end.
   */
  const reFetch = useCallback(() => {
    if (state.error) {
      // Case error, NO Fetch.
      return false;
    } else if (state.isFetching) {
      // Case is fetching, NO Fetch.
      return false;
    } else if (!projectId) {
      // Case no project, NO Fetch.
      return false;
    } else if (scenarios?.length <= 0) {
      // Case no scenarios, NO Fetch.
      return false;
    } else if (state.sites === null) {
      // Case `sites` state is null, YES Fetch.
      return true;
    } else if (projectIdRef.current !== projectId) {
      // Case project change, YES Fetch.
      return true;
    } else if (!arrayIsEqual(scenarioIds, scenarioIdsRef.current)) {
      // Case scenarios change (added or removed), YES Fetch.
      return true;
    } else if (state.refreshScenarios) {
      // Case refreshScenarios state is not null, YES Fetch.
      dispatch({
        type: "REFRESH_SCENARIOS_SET",
        payload: { refreshScenarios: null }
      });
      return true;
      // } else if (state.sites !== sitesRef.current) {
      //   return true;
    } else {
      return false;
    }
  }, [
    projectId,
    scenarioIds,
    scenarios?.length,
    state.error,
    state.isFetching,
    state.sites,
    state.refreshScenarios
  ]);

  /**
   * Effect to fetch the Sites from back-end.
   */
  useEffect(() => {
    if (reFetch()) {
      dispatch({ type: "FETCHING_START" });
      getAllSitesByProject(projectId)
        .then(res => {
          let projectScenarios = [];
          let scenarioIds = [];

          for (const scenario of scenarios) {
            scenarioIds.push(scenario.id);
            projectScenarios.push({
              id: scenario.id,
              name: scenario.name,
              isShared: scenario.isShared || false,
              owner: scenario.owner,
              children: scenario.children,
              parent: scenario.parent,
              type: scenario.type,
              scenarioconfig: scenario.scenarioconfig
            });
          }

          projectIdRef.current = projectId;
          scenarioIdsRef.current = scenarioIds;
          sitesRef.current = res;

          // Filter the Sites to those belonging to
          // the currently active scenario.
          const presentSites = res.filter(site =>
            scenarioIds.includes(site.scenario)
          );

          dispatch({
            type: "SITES_FETCH_SUCCESS",
            payload: {
              sites: presentSites,
              scenarios: projectScenarios,
              projectId: projectId
            }
          });
        })
        .catch(err => {
          const error = typeof err === "object" ? err.message : err;
          dispatch({ type: "FETCH_FAIL", payload: { error: error } });
        });
    }
  }, [projectId, reFetch, scenario?.sites, scenarioIds, scenarios]);

  return (
    <DispatchContext.Provider value={dispatch}>
      <StateContext.Provider value={state}>{children}</StateContext.Provider>
    </DispatchContext.Provider>
  );
}

SitesProvider.propTypes = {
  children: PropTypes.node,
  scenario: PropTypes.object,
  scenarios: PropTypes.array,
  activeScenarioId: PropTypes.number,
  projectId: PropTypes.number
};

/**
 * Hook to access and manage the state of the Sites
 * Context.
 */
const useSites = () => {
  const sitesState = useContext(StateContext);
  const sitesDispatch = useContext(DispatchContext);

  const {
    localSettingsState,
    setLocalVisibility,
    setLocalVisibilityAll,
    setLocalColor
  } = useLocalSettingsContext();

  /**********************
  PRIVATE METHODS
  ***********************/
  /**
   * Method that adds a newly generated site to the context.
   *
   * It fetches the site by id and then it searches the
   * site in the context state. If the site already exists, it
   * replace the site, otherwise it adds the site to the
   * context state.
   *
   * @param {number} siteId Id of the newly generated site.
   */
  const fetchSite = async siteId => {
    sitesDispatch({ type: "FETCHING_START" });
    try {
      const res = await getSite(siteId);

      let fetchedSite = res;
      // fetchedSite.ui_settings.visible = true;

      let updatedSites = [...sitesState.sites];

      const siteIndex = searchObjectIndexInArray(
        fetchedSite.id,
        "id",
        updatedSites
      );

      if (siteIndex >= 0) {
        updatedSites[siteIndex] = fetchedSite;
      } else {
        updatedSites.push(fetchedSite);
      }
      sitesDispatch({
        type: "SITE_FETCH_SUCCESS",
        payload: {
          sites: updatedSites
        }
      });

      return fetchedSite;
    } catch (err) {
      console.error(err);
      const error = typeof err === "object" ? err.message : err;
      sitesDispatch({
        type: "FETCH_FAIL",
        payload: {
          error: error
        }
      });
      return null;
    }
  };

  /**********************
  PUBLIC METHODS
  ***********************/

  /**
   * Method that retrieves the color from local
   * settings of a site by id.
   * Note: The color includes opacity from the color picker (rgba format).
   *
   * @param {number} siteId
   * @returns Color value in rgba format
   */
  const getColor = useCallback(
    siteId => {
      const colorSettings =
        localSettingsState?.localSettings?.map?.color?.sites;

      if (colorSettings && colorSettings[siteId]) {
        // If we have local setting for the site id we take the color from there.
        return colorSettings[siteId];
      } else {
        // Otherwise if no local color settings, we use ui_settings
        // returned by the server.
        const site = sitesState.sites.find(
          p => Number(p.id) === Number(siteId)
        );
        if (site && site?.ui_settings?.color) {
          return site?.ui_settings?.color;
        } else {
          // If no ui_settings either, we return default color.
          return DEFAULT_COLOR;
        }
      }
    },
    [localSettingsState?.localSettings?.map?.color?.sites, sitesState.sites]
  );

  /**
   * Method that sets the color of a given site. Based
   * on the view type it calls the corresponding utility in OL or
   * Cesium.
   * Once set, it stores the setting in the local storage and patches
   * back-end (ui_settings->color).
   *
   * @param {Object} site
   * @param {Number} scenarioId
   * @param {Object} mapContext
   * @param {String} viewType
   * @param {String} value - Color in rgba format (includes opacity)
   * @param {Boolean} setLocal - If true, saves to local storage and backend
   */
  const setColor = (
    site,
    scenarioId,
    mapContext,
    viewType,
    value,
    setLocal
  ) => {
    // Apply color change immediately to the map
    if (viewType === "2D") {
      olSetSiteColor(mapContext.olmap, site.id, scenarioId, value);
    } else if (viewType === "3D") {
      cesiumSetSiteColor(mapContext.viewer, site.id, scenarioId, value);
    }

    if (setLocal) {
      // Save to local storage
      setLocalColor("sites", site.id, value);

      // Save to backend
      modifySite(site.id, { ui_settings: { color: value } }).then(() => {
        // After patching back-end we update the ui_settings->color in
        // the list of sites in the context.
        const sitesToUpdate = JSON.parse(JSON.stringify(sitesState.sites));

        const siteToUpdate = sitesToUpdate.find(
          p => Number(p.id) === Number(site.id)
        );

        if (siteToUpdate) {
          // Ensure ui_settings exists
          if (!siteToUpdate.ui_settings) {
            siteToUpdate.ui_settings = {};
          }
          siteToUpdate.ui_settings.color = value;

          sitesDispatch({
            type: "SITE_FETCH_SUCCESS",
            payload: {
              sites: sitesToUpdate
            }
          });
        }
      });
    }
  };

  /**
   * Method to retrieve the site visibility. It checks if the
   * site id is included in the local settings or not.
   */
  const getVisibility = useCallback(
    siteId => {
      const visibilitySettings =
        localSettingsState?.localSettings?.map?.visibility?.sites;

      if (visibilitySettings) {
        // We return if the path id is included in the local
        // visibility settings or not.
        return visibilitySettings.includes(siteId);
      } else {
        // If no visibility settings, we return false.
        return false;
      }
    },
    [localSettingsState?.localSettings?.map?.visibility?.sites]
  );

  /**
   * Method to generate the site for a
   * given scenario (scenarioId) if passed as a
   * parameter, otherwise it will generate for the
   * active scenario.
   *
   * @param {number} scenarioId Scenario id to generate the site.
   *
   * @returns Promise
   */
  const generateSites = async scenarioId => {
    return await calculateSites(scenarioId || sitesState.activeScenarioId);
  };

  /**
   * Method that returns the active Site, that
   * means the Site corresponing to the active
   * scenario.
   *
   * @returns Site object
   */
  const getActive = () => {
    return sitesState.sites.find(
      item => item.scenario === sitesState.activeScenarioId
    );
  };

  /**
   * Method to retrieve all the sites of a given scenario and
   * add them to the state. This method is used after generating
   * sites for a given scenario.
   *
   * @param {Object} scenario
   * @param {Object} map
   * @param {String} viewType
   */
  const showScenarioGeneratedSites = async (scenario, map, viewType) => {
    try {
      let scenarioSites = []; // Site list returned by BE given a scenario id.
      let fetchedScenarioSites = []; // Sites fetched from BE iterating scenarioSites.
      let updatedSites = clone(sitesState.sites); // Sites in the state, updated with the new fetched ones.

      // Retrieve all the sites of the scenario.
      scenarioSites = await getAllSitesByScenario(scenario.id);

      // Remove the sites from the calculated scenario.
      updatedSites = updatedSites.filter(
        item => Number(item.scenario) !== Number(scenario.id)
      );

      // Fetch all the sites of the scenario by iterating the sites returned by
      // `getAllSitesByScenario`.
      for (const site of scenarioSites) {
        // Update the array of fetched scenarios for later to visualize them.
        fetchedScenarioSites.push(site);
        updatedSites.push(site);
      }

      // Hide old sites and update local storage
      const oldSites = sitesState.sites.filter(item => {
        return Number(item.scenario) === Number(scenario.id);
      });

      const oldIds = [];
      for (const site of oldSites) {
        oldIds.push(site.id);
        if (viewType === "2D") {
          olRemoveSite(map, site.id, scenario.id);
        } else if (viewType === "3D") {
          cesiumRemoveSite(map, site.id, scenario.id);
        }
      }

      // Update local storage visibility (removing old sites)
      setLocalVisibilityAll("sites", oldIds, false);

      // Visualize all the generated sites.
      const ids = [];
      const addSitePromises = [];

      for (const site of fetchedScenarioSites) {
        ids.push(site.id);
        const siteColor = site.ui_settings?.color ?? getColor(site.id);

        if (viewType === "2D") {
          olAddSite(map, site, siteColor, site.scenario);
        } else if (viewType === "3D") {
          // Collect all async operations
          addSitePromises.push(
            cesiumAddSite(map, site, siteColor, site.scenario)
          );
        }
      }

      // Wait for all Cesium sites to be added before zooming
      if (viewType === "3D" && addSitePromises.length > 0) {
        await Promise.all(addSitePromises);
      }

      // Update local storage visibility.
      setLocalVisibilityAll("sites", ids, true);

      // Zoom to the extent of all generated sites
      if (fetchedScenarioSites.length > 0) {
        if (viewType === "2D") {
          olZoomToSitesExtent(map, fetchedScenarioSites);
        } else if (viewType === "3D") {
          await cesiumZoomToSitesExtent(map, fetchedScenarioSites);
        }
      }

      // Update the state with the generated sites.
      sitesDispatch({
        type: "SITE_FETCH_SUCCESS",
        payload: {
          sites: updatedSites
        }
      });
    } catch (err) {
      console.error(err);
    }
  };

  /**
   * Method that sets the visibility of a given Site. Based
   * on the view type it calls the corresponding utility in OL or
   * Cesium.
   * Once set, it stores the setting in the local storage.
   *
   * @param {number} siteId
   * @param {object} map
   * @param {string} viewType
   * @param {boolean} visible
   *
   * @returns True if success
   */
  const setVisibility = async (siteId, map, viewType, visible) => {
    try {
      let siteToShow;

      // Find the site in the context to get its scenario ID
      const site = sitesState.sites.find(s => Number(s.id) === Number(siteId));

      if (!site) {
        throw new Error(`Site with id ${siteId} not found`);
      }

      const scenarioId = site.scenario;

      // Remove existing layer first
      if (viewType === "2D") {
        olRemoveSite(map, siteId, scenarioId);
      } else if (viewType === "3D") {
        cesiumRemoveSite(map, siteId, scenarioId);
      }

      // If the site is visible we fetch it again from BE to get
      // fresh data and add it to the map.
      if (visible === true) {
        siteToShow = await fetchSite(siteId);

        if (!siteToShow) {
          throw new Error(`Site with id ${siteId} not found`);
        }

        // Get the color for the site (includes opacity from color picker)
        const siteColor = getColor(siteId);

        if (viewType === "2D") {
          olAddSite(map, siteToShow, siteColor, scenarioId);
        } else if (viewType === "3D") {
          cesiumAddSite(map, siteToShow, siteColor, scenarioId);
        }
      }

      setLocalVisibility("sites", siteId, visible);
      return true;
    } catch (err) {
      sitesDispatch({
        type: "ERROR_SET",
        payload: { error: typeof err === "object" ? err.message : err }
      });
      throw err;
    }
  };

  /**
   * Method to set the visibility of all the Sites
   * at once. It iterates the list of Sites in the
   * context and fetches them fom BE.
   *
   * @param {object} map
   * @param {string} viewType
   * @param {boolean} visible
   */
  const setVisibilityAll = async (
    map,
    viewType,
    visible,
    scenarioId = null
  ) => {
    try {
      const freshSites = [];

      let allSites = [...sitesState.sites];

      if (scenarioId) {
        let sites = [];
        const scenario = sitesState.scenarios.find(
          item => Number(item.id) === Number(scenarioId)
        );
        if (scenario?.children && scenario?.children.length > 0) {
          // If scenario has children, we look for
          // the Sites of each children of the scenario
          // and set the site list to show/hide.
          for (const childId of scenario.children) {
            const site = sitesState.sites.find(
              item => Number(item.scenario) === Number(childId)
            );
            if (site) sites.push(site);
          }

          allSites = sites;
        } else {
          // If the scenario hasn't children, then we take the RM of
          // the scenario itself.
          allSites = allSites.filter(
            r => Number(r.scenario) === Number(scenarioId)
          );
        }
      }

      // Iterate sites in the state to fetch them
      // if the visibility is going to be `true`.
      for (const site of allSites) {
        if (visible === true) {
          const fetched = await fetchSite(site.id);
          if (fetched) freshSites.push(fetched);
        } else {
          freshSites.push(site);
        }
      }

      const ids = [];

      for (const site of freshSites) {
        ids.push(site.id);
        const siteColor = getColor(site.id);

        if (viewType === "2D" && visible === true) {
          olAddSite(map, site, siteColor, site.scenario);
        } else if (viewType === "2D" && visible === false) {
          olRemoveSite(map, site.id, site.scenario);
        }

        if (viewType === "3D" && visible === true) {
          cesiumAddSite(map, site, siteColor, site.scenario);
        } else if (viewType === "3D" && visible === false) {
          cesiumRemoveSite(map, site.id, site.scenario);
        }
      }

      setLocalVisibilityAll("sites", ids, visible);
    } catch (err) {
      sitesDispatch({
        type: "ERROR_SET",
        payload: { error: typeof err === "object" ? err.message : err }
      });
      throw err;
    }
  };

  function removeSite(siteId) {
    return deleteSite(siteId).then(() => {
      const currentSites = [...sitesState.sites];
      sitesDispatch({
        type: "CUSTOM_SITE_REMOVE",
        payload: {
          sites: currentSites.filter(item => Number(item.id) !== Number(siteId))
        }
      });
    });
  }

  /**
   * Method to change the name of am scenario in the
   * Sites state.
   *
   * @param {Number} scenarioId
   * @param {String} name
   */
  const updateScenarioName = (scenarioId, name) => {
    let tmpScenarios = [...sitesState.scenarios];

    for (let i = 0; i < tmpScenarios.length; i++) {
      if (Number(tmpScenarios[i].id) === Number(scenarioId)) {
        tmpScenarios[i].name = name;
      }
    }
    sitesDispatch({
      type: "SCENARIO_NAME_UPDATE",
      payload: { scenarios: tmpScenarios }
    });
  };

  const setSiteNew = (id, value) => {
    let tmpSites = [...sitesState.sites];
    for (let i = 0; i < tmpSites.length; i++) {
      if (Number(tmpSites[i].id) === Number(id)) {
        tmpSites[i].isNew = value;
      }
    }
    sitesDispatch({
      type: "SITE_UPDATE",
      payload: { sites: tmpSites }
    });
  };

  /**
   * Method to zoom the map to the extent of all visible sites belonging to a scenario.
   * Filters sites by scenario ID (including children scenarios if applicable)
   * and then filters by visibility before zooming to their combined extent.
   *
   * @param {Number} scenarioId - The scenario ID to zoom to
   * @param {Object} map - The map instance (OL or Cesium)
   * @param {String} viewType - The view type ("2D" or "3D")
   * @param {Object} options - Optional zoom configuration (padding, duration, maxZoom)
   * @returns {Boolean} - Returns true if zoom was successful, false otherwise
   */
  const zoomToScenarioSites = (scenarioId, map, viewType, options = {}) => {
    try {
      // Find the scenario
      const scenario = sitesState.scenarios.find(
        item => Number(item.id) === Number(scenarioId)
      );

      if (!scenario) {
        return false;
      }

      // Get all sites for this scenario
      let scenarioSites = [];

      if (scenario.children && scenario.children.length > 0) {
        // If scenario has children, collect sites from all children
        for (const childId of scenario.children) {
          const childSites = sitesState.sites.filter(
            item => Number(item.scenario) === Number(childId)
          );
          scenarioSites.push(...childSites);
        }
      } else {
        // Otherwise, get sites from the scenario itself
        scenarioSites = sitesState.sites.filter(
          item => Number(item.scenario) === Number(scenarioId)
        );
      }

      // Filter only visible sites
      const visibleSites = scenarioSites.filter(site => getVisibility(site.id));

      // Check if we have visible sites to zoom to
      if (visibleSites.length === 0) {
        console.warn(
          `No visible sites found for scenario ${scenarioId}. Make sure sites are visible before zooming.`
        );
        return false;
      }

      // Call the appropriate zoom utility based on view type
      if (viewType === "2D") {
        return olZoomToSitesExtent(map, visibleSites, options);
      } else if (viewType === "3D") {
        cesiumZoomToSitesExtent(map, visibleSites, options);
        return true;
      }
    } catch (err) {
      console.error("Error zooming to scenario sites:", err);
      return false;
    }
  };

  return [
    sitesState,
    {
      generateSites: generateSites,
      getActiveScenario: getActive,
      setSiteColor: setColor,
      getSiteColor: getColor,
      getSiteVisibility: getVisibility,
      setSiteVisibility: setVisibility,
      setSiteAllVisibility: setVisibilityAll,
      removeSite: removeSite,
      updateScenarioName: updateScenarioName,
      setSiteNew: setSiteNew,
      showScenarioGeneratedSites: showScenarioGeneratedSites,
      zoomToScenarioSites: zoomToScenarioSites
    }
  ];
};

export { useSites };

export default SitesProvider;
